/**
 * Crew Daily Report: what a foreman fills out on a phone for one project and one day,
 * and what the office reviews, approves and exports. Pure data + rules; the server
 * stores it (storage `daily_reports`), the web app edits it.
 */
import type { PayItem } from "./markup/types.js";
import { phaseCodeFor } from "./phase-codes.js";

export const DAILY_REPORT_STATUSES = ["draft", "submitted", "approved"] as const;
export type DailyReportStatus = (typeof DAILY_REPORT_STATUSES)[number];

/** Units a paving / site-work crew reports production in. */
/** Includes FDOT pay-item units (AC acre, GM gross mile, NM net mile, ED each-day, DA day, FD, LB, MO) so SOV lines deduct. */
export const DAILY_REPORT_UNITS = ["TN", "SY", "LF", "CY", "EA", "SF", "LS", "HR", "GAL", "LD", "AC", "GM", "NM", "ED", "DA", "FD", "LB", "MO"] as const;
export type DailyReportUnit = (typeof DAILY_REPORT_UNITS)[number];

export const DELAY_CAUSES = ["rain", "wet ground", "material", "equipment", "inspection", "owner/GC", "utility", "other"] as const;
export type DelayCause = (typeof DELAY_CAUSES)[number];

/** `phase` is a Watson phase code base number (e.g. "7620"), see phase-codes.ts. */
export interface CrewLine { name: string; role: string; hours: number; phase?: string }
/** No phase: Watson equipment is owned (a stored `phase` from older reports is dropped on save). */
export interface EquipmentLine { name: string; hours: number; notes: string }
export interface WeatherInfo {
  summary: string;
  highF: number | null;
  lowF: number | null;
  precipIn: number | null;
  wind: string;
  /** "nws" when auto-filled (then possibly edited), "manual" otherwise. */
  source: "nws" | "manual";
  fetchedAt?: string;
}
export interface DelayLine { cause: DelayCause; hours: number; notes: string }
export interface WorkLine {
  description: string;
  location: string;
  quantity: number;
  unit: DailyReportUnit;
  payItem?: PayItem;
  /** Takeoff row this quantity counts against (document + measurement id). */
  takeoff?: { documentId: string; measurementId: string; label: string };
  /** Schedule-of-values line this quantity counts toward. */
  sovItemId?: string;
  /** Watson phase code. */
  phase?: string;
}
export interface MaterialLine { material: string; supplier: string; ticket: string; truck: string; quantity: number; unit: DailyReportUnit; phase?: string;
  /** Photo of the truck ticket: id of a PhotoRef on this report. */
  photoId?: string }
export interface SubcontractorLine { company: string; headcount: number; hours: number; work: string; phase?: string }
export interface VisitorLine { name: string; company: string; purpose: string; result: string }
export interface SafetyInfo { toolboxTalk: string; incident: boolean; incidentNotes: string; notes: string }
export interface PhotoRef { id: string; key: string; name: string; contentType: string; size: number; caption: string; addedAt: string; /** Set when the photo was dropped on a sheet as a Photo pin. */ pinId?: string; /** Sheet number of the pin, for the report. */ sheet?: string; /** Content hash, used to avoid adding the same picture twice. */ sha256?: string }
export interface Signature { name: string; /** PNG data URL from the signature pad. */ image: string; signedAt: string }

export interface DailyReport {
  id: string;
  projectId: string;
  /** Local work date, YYYY-MM-DD. */
  date: string;
  shift: "day" | "night";
  foreman: string;
  status: DailyReportStatus;
  crew: CrewLine[];
  equipment: EquipmentLine[];
  weather: WeatherInfo;
  delays: DelayLine[];
  work: WorkLine[];
  materials: MaterialLine[];
  subcontractors: SubcontractorLine[];
  visitors: VisitorLine[];
  safety: SafetyInfo;
  notes: string;
  photos: PhotoRef[];
  signature?: Signature;
  submittedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  /** Login that started the report (foremen may only edit their own). */
  createdBy?: { id: string; name: string };
  /** Set when a supervisor/PM sends a submitted report back to draft. */
  returnedAt?: string;
  returnedBy?: string;
  returnNote?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** The fields a client may send (server owns id, timestamps, version, photos). */
export type DailyReportInput = Partial<Omit<DailyReport, "id" | "createdAt" | "updatedAt" | "version" | "photos">>;

export class DailyReportError extends Error {
  constructor(public readonly problems: string[]) {
    super(problems.join(" "));
    this.name = "DailyReportError";
  }
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown, max = 500) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const num = (v: unknown, max = 1e9): number => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), max) : 0;
};
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const arr = (v: unknown, max = 200): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === "object").slice(0, max) : [];
const oneOf = <T extends string>(v: unknown, list: readonly T[], fallback: T): T => {
  const s = typeof v === "string" ? v.trim() : "";
  const hit = list.find((x) => x.toLowerCase() === s.toLowerCase());
  return hit ?? fallback;
};
/** Adds `phase` when the client sent a usable code. */
const withPhase = <T extends object>(line: T, v: unknown): T & { phase?: string } => {
  const code = typeof v === "string" ? v.trim().slice(0, 20) : "";
  return code ? { ...line, phase: code } : line;
};
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

export function isValidDate(date: string): boolean {
  if (!DATE_RE.test(date)) return false;
  const d = new Date(`${date}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === date;
}

export function emptyWeather(): WeatherInfo {
  return { summary: "", highF: null, lowF: null, precipIn: null, wind: "", source: "manual" };
}

/**
 * Cleans client input into the editable part of a report. Blank lines (no name /
 * description) are dropped so a half-filled phone form saves cleanly.
 */
export function normalizeDailyReportInput(input: unknown, base?: DailyReport): Omit<DailyReport, "id" | "createdAt" | "updatedAt" | "version" | "photos"> {
  const i = obj(input);
  const has = (k: string) => Object.prototype.hasOwnProperty.call(i, k);
  const problems: string[] = [];
  const date = has("date") ? str(i.date, 10) : base?.date ?? "";
  if (!isValidDate(date)) problems.push("Date must be YYYY-MM-DD.");
  const projectId = has("projectId") ? str(i.projectId, 64) : base?.projectId ?? "";
  if (!projectId) problems.push("Project is required.");
  if (problems.length) throw new DailyReportError(problems);

  const w = has("weather") ? obj(i.weather) : (base?.weather as unknown as Record<string, unknown>) ?? {};
  const s = has("safety") ? obj(i.safety) : (base?.safety as unknown as Record<string, unknown>) ?? {};
  const pick = <T>(k: keyof DailyReport, f: (v: unknown) => T): T => (has(k) ? f(i[k]) : base ? (base[k] as unknown as T) : f(undefined));

  const payItem = (v: unknown): PayItem | undefined => {
    const p = obj(v);
    return typeof p.number === "string" && p.number.trim() ? { number: str(p.number, 40), name: str(p.name, 200), unit: str(p.unit, 20) } : undefined;
  };
  const takeoff = (v: unknown) => {
    const t = obj(v);
    return typeof t.documentId === "string" && typeof t.measurementId === "string"
      ? { documentId: str(t.documentId, 64), measurementId: str(t.measurementId, 64), label: str(t.label, 200) } : undefined;
  };

  const out = {
    projectId,
    date,
    shift: pick("shift", (v) => oneOf(v, ["day", "night"] as const, "day")),
    foreman: pick("foreman", (v) => str(v, 120)),
    status: pick("status", (v) => oneOf(v, DAILY_REPORT_STATUSES, "draft")),
    crew: pick("crew", (v) => arr(v).map((c) => withPhase({ name: str(c.name, 120), role: str(c.role, 80), hours: num(c.hours, 24) }, c.phase)).filter((c) => c.name)),
    equipment: pick("equipment", (v) => arr(v).map((e) => ({ name: str(e.name, 120), hours: num(e.hours, 24), notes: str(e.notes) })).filter((e) => e.name)),
    weather: {
      summary: str(w.summary, 300), highF: numOrNull(w.highF), lowF: numOrNull(w.lowF), precipIn: numOrNull(w.precipIn), wind: str(w.wind, 80),
      source: oneOf(w.source, ["nws", "manual"] as const, "manual"), ...(typeof w.fetchedAt === "string" ? { fetchedAt: w.fetchedAt } : {}),
    },
    delays: pick("delays", (v) => arr(v).map((d) => ({ cause: oneOf(d.cause, DELAY_CAUSES, "other"), hours: num(d.hours, 24), notes: str(d.notes) })).filter((d) => d.hours > 0 || d.notes)),
    work: pick("work", (v) => arr(v).map((l) => {
      const line: WorkLine = { description: str(l.description, 300), location: str(l.location, 200), quantity: num(l.quantity), unit: oneOf(l.unit, DAILY_REPORT_UNITS, "EA") };
      const p = payItem(l.payItem); if (p) line.payItem = p;
      const t = takeoff(l.takeoff); if (t) line.takeoff = t;
      if (typeof l.sovItemId === "string" && l.sovItemId.trim()) line.sovItemId = str(l.sovItemId, 64);
      return withPhase(line, l.phase);
    }).filter((l) => l.description || l.payItem || l.sovItemId)),
    materials: pick("materials", (v) => arr(v, 500).map((m) => withPhase({ material: str(m.material, 200), supplier: str(m.supplier, 120), ticket: str(m.ticket, 60), truck: str(m.truck, 60), quantity: num(m.quantity), unit: oneOf(m.unit, DAILY_REPORT_UNITS, "TN"), ...(typeof m.photoId === "string" && m.photoId.trim() ? { photoId: str(m.photoId, 64) } : {}) }, m.phase)).filter((m) => m.material || m.ticket || m.photoId)),
    subcontractors: pick("subcontractors", (v) => arr(v).map((x) => withPhase({ company: str(x.company, 120), headcount: Math.round(num(x.headcount, 500)), hours: num(x.hours, 24), work: str(x.work) }, x.phase)).filter((x) => x.company)),
    visitors: pick("visitors", (v) => arr(v).map((x) => ({ name: str(x.name, 120), company: str(x.company, 120), purpose: str(x.purpose, 200), result: str(x.result, 300) })).filter((x) => x.name || x.company)),
    safety: { toolboxTalk: str(s.toolboxTalk, 300), incident: s.incident === true || s.incident === "true", incidentNotes: str(s.incidentNotes, 2000), notes: str(s.notes, 2000) },
    notes: pick("notes", (v) => str(v, 5000)),
  } as Omit<DailyReport, "id" | "createdAt" | "updatedAt" | "version" | "photos">;
  const sig = has("signature") ? obj(i.signature) : base?.signature ? (base.signature as unknown as Record<string, unknown>) : {};
  if (typeof sig.image === "string" && sig.image) {
    // Used to be dropped silently when it didn't fit, so the foreman only saw "signature required".
    if (!/^data:image\/png;base64,[A-Za-z0-9+/=]{40,}$/.test(sig.image)) throw new DailyReportError(["The signature didn't come through. Tap Clear and sign again."]);
    if (sig.image.length > 1_500_000) throw new DailyReportError(["The signature image is too large. Tap Clear and sign again."]);
    out.signature = { name: str(sig.name, 120) || out.foreman, image: sig.image, signedAt: typeof sig.signedAt === "string" ? sig.signedAt : new Date().toISOString() };
  }
  if (base?.submittedAt) out.submittedAt = base.submittedAt;
  if (base?.approvedAt) out.approvedAt = base.approvedAt;
  if (base?.approvedBy) out.approvedBy = base.approvedBy;
  if (base?.createdBy) out.createdBy = base.createdBy;
  if (base?.returnedAt) { out.returnedAt = base.returnedAt; out.returnedBy = base.returnedBy; out.returnNote = base.returnNote; }
  return out;
}

/** Problems that block moving a report to `submitted` (and `approved`). */
export function submitProblems(r: Pick<DailyReport, "foreman" | "signature" | "crew" | "work" | "delays" | "notes">): string[] {
  const p: string[] = [];
  if (!r.foreman) p.push("Foreman name is required.");
  if (!r.signature) p.push("Foreman signature is required to submit.");
  if (r.crew.length === 0) p.push("Add at least one crew member.");
  if (r.work.length === 0 && r.delays.length === 0 && !r.notes) p.push("Add work performed, a delay, or a note.");
  return p;
}

/** Applies a status change with its rules; returns the stamped fields. */
export function applyStatus(r: DailyReport, status: DailyReportStatus, actor: string, now = new Date().toISOString()): DailyReport {
  if (status === r.status) return r;
  if (status !== "draft") {
    const p = submitProblems(r);
    if (p.length) throw new DailyReportError(p);
  }
  if (status === "approved" && r.status === "draft") throw new DailyReportError(["Only a submitted report can be approved."]);
  const next: DailyReport = { ...r, status };
  if (status === "submitted") { next.submittedAt = now; delete next.approvedAt; delete next.approvedBy; delete next.returnedAt; delete next.returnedBy; delete next.returnNote; }
  if (status === "approved") { next.approvedAt = now; next.approvedBy = actor; }
  if (status === "draft") { delete next.approvedAt; delete next.approvedBy; }
  return next;
}

export interface DailyReportTotals {
  crewCount: number;
  crewHours: number;
  equipmentHours: number;
  delayHours: number;
  rainHours: number;
  subcontractorHours: number;
  /** Production by unit, e.g. { TN: 412.5, SY: 3100 }. */
  production: Record<string, number>;
  /** Material received by unit, plus ticket count. */
  received: Record<string, number>;
  ticketCount: number;
}

const round = (n: number, d = 2) => Math.round(n * 10 ** d) / 10 ** d;

export function dailyReportTotals(r: Pick<DailyReport, "crew" | "equipment" | "delays" | "work" | "materials" | "subcontractors">): DailyReportTotals {
  const sum = <T>(xs: T[], f: (x: T) => number) => round(xs.reduce((a, x) => a + f(x), 0));
  const byUnit = <T extends { unit: string; quantity: number }>(xs: T[]) => {
    const m: Record<string, number> = {};
    for (const x of xs) m[x.unit] = round((m[x.unit] ?? 0) + x.quantity, 3);
    return m;
  };
  return {
    crewCount: r.crew.length,
    crewHours: sum(r.crew, (c) => c.hours),
    equipmentHours: sum(r.equipment, (e) => e.hours),
    delayHours: sum(r.delays, (d) => d.hours),
    rainHours: sum(r.delays.filter((d) => d.cause === "rain" || d.cause === "wet ground"), (d) => d.hours),
    subcontractorHours: sum(r.subcontractors, (s) => s.hours * Math.max(1, s.headcount)),
    production: byUnit(r.work),
    received: byUnit(r.materials),
    ticketCount: r.materials.filter((m) => m.ticket).length,
  };
}

export const formatUnitTotals = (m: Record<string, number>) => Object.entries(m).map(([u, q]) => `${q.toLocaleString("en-US")} ${u}`).join("; ");

// ---------------------------------------------------------------- CSV / XLSX rows

export type Cell = string | number | null;
export interface Sheet { name: string; rows: Cell[][] }

/** Workbook layout shared by CSV (first sheet) and XLSX (all sheets). */
/** `sovLabel` turns a work line's SOV item id into "itemNo description" for the Work sheet. */
export function dailyReportSheets(reports: DailyReport[], projectName: (id: string) => string, sovLabel: (id: string) => string = () => ""): Sheet[] {
  const head = (r: DailyReport): Cell[] => [r.date, projectName(r.projectId), r.foreman];
  const H = ["Date", "Project", "Foreman"];
  const sorted = [...reports].sort((a, b) => a.date.localeCompare(b.date) || projectName(a.projectId).localeCompare(projectName(b.projectId)));
  return [
    { name: "Reports", rows: [[...H, "Shift", "Status", "Crew", "Crew hrs", "Equip hrs", "Rain/wet hrs", "Total delay hrs", "Production", "Received", "Tickets", "Weather", "High F", "Low F", "Precip in", "Incident", "Submitted", "Approved by", "Notes"],
      ...sorted.map((r) => { const t = dailyReportTotals(r); return [...head(r), r.shift, r.status, t.crewCount, t.crewHours, t.equipmentHours, t.rainHours, t.delayHours, formatUnitTotals(t.production), formatUnitTotals(t.received), t.ticketCount, r.weather.summary, r.weather.highF, r.weather.lowF, r.weather.precipIn, r.safety.incident ? "YES" : "", r.submittedAt ?? "", r.approvedBy ?? "", r.notes]; })] },
    { name: "Crew", rows: [[...H, "Phase", "Name", "Role", "Hours"], ...sorted.flatMap((r) => r.crew.map((c) => [...head(r), phaseCodeFor(c.phase ?? "", "labor"), c.name, c.role, c.hours]))] },
    { name: "Equipment", rows: [[...H, "Equipment", "Hours", "Notes"], ...sorted.flatMap((r) => r.equipment.map((e) => [...head(r), e.name, e.hours, e.notes]))] },
    { name: "Work", rows: [[...H, "Phase", "SOV item", "Pay item", "Description", "Location", "Quantity", "Unit", "Takeoff"], ...sorted.flatMap((r) => r.work.map((w) => [...head(r), phaseCodeFor(w.phase ?? "", "labor"), w.sovItemId ? sovLabel(w.sovItemId) || "(removed SOV item)" : "", w.payItem?.number ?? "", w.description || w.payItem?.name || "", w.location, w.quantity, w.unit, w.takeoff?.label ?? ""]))] },
    { name: "Materials", rows: [[...H, "Phase", "Material", "Supplier", "Ticket", "Truck", "Quantity", "Unit", "Ticket photo"], ...sorted.flatMap((r) => r.materials.map((m) => [...head(r), phaseCodeFor(m.phase ?? "", "material"), m.material, m.supplier, m.ticket, m.truck, m.quantity, m.unit, m.photoId ? "yes" : ""]))] },
    { name: "Delays", rows: [[...H, "Cause", "Hours", "Notes"], ...sorted.flatMap((r) => r.delays.map((d) => [...head(r), d.cause, d.hours, d.notes]))] },
    { name: "Subs & Visitors", rows: [[...H, "Type", "Company", "Name/Headcount", "Hours", "Work/Purpose", "Result", "Phase"],
      ...sorted.flatMap((r) => [...r.subcontractors.map((s): Cell[] => [...head(r), "Subcontractor", s.company, s.headcount, s.hours, s.work, "", phaseCodeFor(s.phase ?? "", "subcontractor")]), ...r.visitors.map((v): Cell[] => [...head(r), "Visitor", v.company, v.name, null, v.purpose, v.result, ""])])] },
  ];
}

export function toCsv(rows: Cell[][]): string {
  const cell = (c: Cell) => {
    if (c === null || c === undefined) return "";
    let s = String(c);
    if (/^[=+\-@]/.test(s) && typeof c === "string") s = `'${s}`; // no spreadsheet formulas from field input
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";
}
